import { Component, NgZone, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { MenuController, ModalController, NavController } from '@ionic/angular';
import { CataleanLocalizationService } from 'catalean-localization';
import { environment } from '../environments/environment';
import { Subject, Subscription, take } from 'rxjs';
import { DeviceResourcesService } from 'catalean-local-files';
import { CataleanAuthenticationService } from 'catalean-authentication';
import { FilterManagerService } from 'catalean-provider';
import { Filter } from 'catalean-models';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { CurrentPlatformService } from './services/current-platform.service';
import { FinancialFormComponent } from './shared/components/financial-form/financial-form.component';
import { MetroStepperParentService } from './pages/metro/metro-stepper-parent.service';
import { CataleanWishlistService } from 'catalean-wishlist';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { CataleanHamburgerMenuComponent } from 'catalean-hamburger-menu';

const env = environment;

declare let window: any;
window.pdfWorkerSrc = './assets/pdf.worker.min.js';
@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
	public readonly VERSION_PREFIX: string = 'Version ';
	public version: string = '';
	public openMenuSubject = new Subject<void>();
	private wasDownloadPaused = false;
	private resumeSub: Subscription;
	private pauseSub: Subscription;
	private metroStepperService = inject(MetroStepperParentService);

	rootFilterChildren$ = this.filterService.getRootFilterChildren();
	activeFilter$ = this.filterService.getSelectedMetroFilter();
	rootFilter$ = this.filterService.getRootFilter();

	@ViewChild('hamburgerMenu') hamburgerMenu: CataleanHamburgerMenuComponent;

	tokenTimestampInit: number = 0;

	constructor(
		public platform: CurrentPlatformService,
		private menuController: MenuController,
		public localizationService: CataleanLocalizationService,
		private navCtrl: NavController,
		private ngZone: NgZone,
		private deviceResourcesService: DeviceResourcesService,
		private authService: CataleanAuthenticationService,
		private filterService: FilterManagerService,
		private router: Router,
		private modalController: ModalController,
		private wishList: CataleanWishlistService
	) {
		this.initializeApp();
	}

	initializeApp() {
		this.platform.ready().then(async () => {
			// devo settarlo inizialmente perchè altrimenti
			// la chiamata al catalean cache per il current version si rompe
			this.localizationService.setDefaultLang(env.defaultLocale);

			//devo settarlo altrimenti non vedo locale nella login
			this.localizationService.AvailableLocales = env.availableLocales;

			this.wishList.init().subscribe();

			// Su iOS: prima ATT nativo, poi iubenda
			if (Capacitor.getPlatform() === 'ios') {
				await this.requestATT();
			}
			this.initIubenda();
		});
	}

	/**
	 * Richiede il permesso App Tracking Transparency su iOS.
	 * Il prompt appare una sola volta, poi iOS ricorda la scelta.
	 */
	private async requestATT(): Promise<void> {
		try {
			const { status } = await AppTrackingTransparency.getStatus();
			if (status === 'notDetermined') {
				await AppTrackingTransparency.requestPermission();
			}
		} catch (e) {
			console.warn('[ATT] Non disponibile su questa piattaforma', e);
		}
	}

	private initIubenda(): void {
		const siteId = env.analytics.iubendaSiteId;
		const w = window as any;
		w._iub = w._iub || [];
		w._iub.csConfiguration = {
			siteId,
			cookiePolicyId: siteId,
			lang: 'it',
			storage: { useSiteId: true },
			//Sono le categorie di consenso iubenda (standard TCF/iubenda):
			// 1 — Cookie necessari/tecnici (funzionamento base del sito)
			// 5 — Misurazione/Analytics (tracciamento statistico, es. Google Analytics/GTM)
			purposes: '1,5',
			banner: {
				acceptButtonDisplay: true,
				closeButtonRejects: true,
				customizeButtonDisplay: true,
				explicitWithdrawal: true,
				position: 'bottom',
			},
			callback: {
				onConsentGiven: () => window.dispatchEvent(new CustomEvent('iubenda-consent-given')),
				onConsentRejected: () => window.dispatchEvent(new CustomEvent('iubenda-consent-rejected')),
			},
		};

		// Autoblocking script
		const autoblock = document.createElement('script');
		autoblock.src = `https://cs.iubenda.com/autoblocking/${siteId}.js`;
		document.head.appendChild(autoblock);

		// iubenda CS script
		const cs = document.createElement('script');
		cs.src = '//cdn.iubenda.com/cs/iubenda_cs.js';
		cs.charset = 'UTF-8';
		cs.async = true;
		document.head.appendChild(cs);
	}

	ngOnDestroy(): void {
		this.resumeSub?.unsubscribe();
		this.pauseSub?.unsubscribe();
	}

	ngOnInit() {
		App.addListener('appUrlOpen', (value) => {
			if (value.url.includes(environment.appIdAndroid.toLocaleLowerCase() + '://') || value.url.includes(environment.appIdIos + '://')) {
				console.log('redirecting from universal link: ', value.url);
				if (Capacitor.getPlatform() === 'android') {
					location.href = 'http://localhost/' + value.url.replace(environment.appIdAndroid.toLocaleLowerCase() + '://', '');
				}
				if (Capacitor.getPlatform() === 'ios') {
					location.href = 'capacitor://localhost/' + value.url.replace(environment.appIdIos + '://', '');
					Browser.close();
				}
			} else {
				this.router.navigate(['initializer']);
			}
		});

		if (this.platform.is('mobile')) {
			this.platform.ready().then(() => {
				App.getInfo()
					.then(({ version }) => {
						this.version = this.VERSION_PREFIX + version;
					})
					.catch((err) => {
						console.log(err);
						this.version = this.VERSION_PREFIX + environment.version;
					});
			});
		} else {
			this.version = this.VERSION_PREFIX + environment.version;
		}

		const tokenTimestamp = localStorage.getItem('id_token_stored_at');
		if (tokenTimestamp) {
			const timestamp = parseInt(tokenTimestamp, 10);
			this.tokenTimestampInit = timestamp;
		}

		if (this.platform.is('cordova')) {
			this.resumeSub = this.platform.resume.subscribe(() => {
				if (!this.wasDownloadPaused) {
					this.ngZone.run(() => {
						this.deviceResourcesService.doDownloadRemoteResources();
					});
				}
			});
			this.pauseSub = this.platform.pause.subscribe(() => {
				this.wasDownloadPaused = this.deviceResourcesService.StopDownload;
				this.deviceResourcesService.StopDownload = true;
			});
		}
	}

	goTo(filter: Filter) {
		this.navCtrl.navigateRoot(['metro']).then(() => {
			if (!filter['isRoot']) {
				this.filterService.setSelectedMetroFilter(filter);
				const horizontalFilter = filter.children.find((child) => child.align === 'horizontal');
				if (horizontalFilter) {
					this.filterService.setHorizontalFilter(horizontalFilter);
				}

				if (filter.name === this.metroStepperService.companyProfile) {
					this.navCtrl.navigateForward(['metro', this.metroStepperService.companyProfile]);
				} else {
					this.filterService
						.getAllMetroFilters()
						.pipe(take(1))
						.subscribe((allMetroFilters) => {
							this.metroStepperService.resetStack();
							//popolo il metro stepper per avere l'alberatura corretta se apro il menu hamburger da product grid
							for (const featureId of this.hamburgerMenu.visibleFilterUUID) {
								const filter = allMetroFilters.find((f) => f.featureId === featureId);
								if (filter) {
									this.metroStepperService.addFilterInStack(featureId, filter.label);
								}
							}
							this.metroStepperService.addFilterInStack(filter.featureId, filter.label);
							this.navCtrl.navigateForward(['product-grid']);
						});
				}
			} else {
				this.metroStepperService.resetStack();
				this.filterService.setSelectedMetroFilter(filter);
				this.navCtrl.navigateForward(['metro']);
			}

			window.setTimeout(() => this.menuController.close('catalean-menu'), 600);
			return;
		});
	}

	async logout() {
		this.deviceResourcesService.StopDownload = true;

		await this.authService.logout();
		if (environment.logoutDataRetentionMode === 3) {
			await this.deviceResourcesService.deleteAllAssets();
		}
		//se non lo metti, non navigando alla login l'utente può
		//chiudere il pop-up dell'auth e rimanere in app girando senza problemi
		this.navCtrl.navigateRoot(['login']);
	}

	onMenuOpen() {
		this.openMenuSubject.next();
	}

	async openFinancialCalculation() {
		const modal = await this.modalController.create({
			component: FinancialFormComponent,
			componentProps: {},
			cssClass: 'financial-form fullscreen',
		});
		modal.present();
	}

	openConsume() {}
}
